import { optimizedApi } from '@/utils/api/optimized-api';

export interface ClaimsReportSummary {
  total: number;
  pending: number;
  approved: number;
  declined: number;
  approval_rate: number;
}

export interface ClaimsReportAgent {
  agent_name: string;
  total: number;
  pending: number;
  approved: number;
  declined: number;
  approval_rate: number;
}

export interface ClaimsReportDay {
  day: string;
  total: number;
  pending: number;
  approved: number;
  declined: number;
}

export interface ClaimsReportApprover {
  approver_name: string;
  total: number;
  approved: number;
  declined: number;
}

export interface ClaimsReport {
  summary: ClaimsReportSummary;
  by_agent: ClaimsReportAgent[];
  by_day: ClaimsReportDay[];
  by_approver: ClaimsReportApprover[];
  from_date: string;
  to_date: string;
}

export interface LeadClaim {
  uuid: string;
  lead_uuid: string;
  lead_name: string | null;
  lead_phone: string | null;
  claimed_by_user_name: string | null;
  current_owner_name: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'declined';
  admin_note: string | null;
  actioned_by_user_name: string | null;
  created_at: string;
  actioned_at: string | null;
  disposition_l1_label: string | null;
  disposition_l2_label: string | null;
  disposition_l3_label: string | null;
}

export interface InAppNotification {
  uuid: string;
  type: string;
  title: string;
  body: string | null;
  lead_uuid: string | null;
  claim_uuid: string | null;
  is_read: boolean;
  created_at: string;
}

// Why an agent may ask for someone else's customer. Keep in step with CLAIM_REASONS in
// zeroharm-suite-api/app/api/v1/lead_claims.py (the API rejects anything else).
export const CLAIM_REASONS: { code: string; label: string }[] = [
  { code: 'customer_called_me', label: 'Customer called me directly' },
  { code: 'customer_asked_for_me', label: 'Customer asked for me by name' },
  { code: 'existing_relationship', label: 'I already look after this customer' },
  { code: 'wrong_assignment', label: 'Assigned to the wrong agent or team' },
  { code: 'manager_instruction', label: 'My manager asked me to take it' },
  { code: 'other', label: 'Other (say why)' },
];

export const claimsService = {
  submitClaim: (lead_uuid: string, reason_code: string, note?: string) =>
    optimizedApi.post('/lead-claims', { lead_uuid, reason_code, reason: note || null }),

  getPendingClaims: () =>
    optimizedApi.get<LeadClaim[]>('/lead-claims/pending'),

  getAllClaims: (from_date?: string, to_date?: string) => {
    const params = new URLSearchParams();
    if (from_date) params.set('from_date', from_date);
    if (to_date) params.set('to_date', to_date);
    const qs = params.toString();
    return optimizedApi.get<LeadClaim[]>(`/lead-claims/all${qs ? `?${qs}` : ''}`);
  },

  getMyClaims: () =>
    optimizedApi.get<LeadClaim[]>('/lead-claims/my'),

  getPendingCount: () =>
    optimizedApi.get<{ count: number }>('/lead-claims/pending-count'),

  // override: approve even though the current owner recently worked the customer (the
  // first attempt comes back held, naming who worked them and when).
  approve: (claim_uuid: string, admin_note?: string, override = false) =>
    optimizedApi.put(`/lead-claims/${claim_uuid}/approve`, { admin_note: admin_note || null, override }),

  decline: (claim_uuid: string, admin_note?: string) =>
    optimizedApi.put(`/lead-claims/${claim_uuid}/decline`, { admin_note: admin_note || null }),

  getReports: (from_date?: string, to_date?: string) => {
    const params = new URLSearchParams();
    if (from_date) params.set('from_date', from_date);
    if (to_date) params.set('to_date', to_date);
    const qs = params.toString();
    return optimizedApi.get<ClaimsReport>(`/lead-claims/reports${qs ? `?${qs}` : ''}`);
  },
};

export const notificationsService = {
  getUnreadCount: () =>
    optimizedApi.get<{ count: number }>('/notifications/unread-count'),

  list: () =>
    optimizedApi.get<InAppNotification[]>('/notifications'),

  markRead: (uuid: string) =>
    optimizedApi.put(`/notifications/${uuid}/read`, {}),

  markAllRead: () =>
    optimizedApi.put('/notifications/mark-all-read', {}),
};
